import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { CheckedInUser } from "src/components/CheckedIn/checkedInUser";
import { CheckedInIcon } from "src/components/CheckedIn/CheckedInIcon";
import { CheckedInAction } from "src/components/CheckedIn/CheckedInSiteStyles";
import { DefaultBodyAndMindFeatureName } from "../BodyAndMindList";
import { CategoryContents, CheckIn, getViewerCategory, getViewerHome, Home, Post, updateCheckIn } from "./viewerApi";
import { childrenOf, descendantIds, ordered, postsAtLevel } from "./viewerData";
import { ViewerPost } from "./ViewerPost";
import {
    DirectoryGrid, DirectoryTile, Hero, PostCollection, SearchBox, SectionHeading, ViewerBar,
    ViewerMessage, ViewerRoot, ViewerSection
} from "./ViewerStyles";

export default function BodyAndMindViewer({ user }: { user: CheckedInUser }): React.ReactElement {
    const history = useHistory();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const categoryId: string | null = params.get("category");
    const directoryId: string | null = params.get("directory");
    const postId: string | null = params.get("post");
    const requestedView: string | null = params.get("view");
    const view: string | null = requestedView === "all" || requestedView === "recent" ? requestedView : null;
    const [home, setHome] = useState<Home | null>(null);
    const [cache, setCache] = useState<Record<string, CategoryContents>>({});
    const [homeError, setHomeError] = useState<boolean>(false);
    const [categoryError, setCategoryError] = useState<boolean>(false);
    const [retry, setRetry] = useState<number>(0);
    const [query, setQuery] = useState<string>("");
    const [checkInError, setCheckInError] = useState<string>("");
    const [checkInBusy, setCheckInBusy] = useState<boolean>(false);
    const mutation = useRef<boolean>(false);
    const featureName: string = user.customBodyAndMindName || DefaultBodyAndMindFeatureName;
    const category = home?.categories.find(item => item.uuid === categoryId);
    const contents: CategoryContents | undefined = categoryId ? cache[categoryId] : undefined;
    const directories = contents?.subcategories || [];
    const directory = directories.find(item => item.uuid === directoryId && !item.deleted_at);
    const title: string = directory?.categoryName || category?.name || featureName;
    const posts: Post[] = categoryId ? contents?.posts || [] : home?.uncategorisedContent || [];
    const selectedPost: Post | undefined = posts.find(post => post.uuid === postId);
    const visibleChildren = childrenOf(directories, directoryId);
    const subtree: Set<string> | null = directoryId ? descendantIds(directories, directoryId) : null;
    const subtreePosts: Post[] = subtree ? posts.filter(post => subtree.has(post.subcategoryUuid)) : posts;
    const recent: Post[] = (contents?.recentlyAddedPostUuids || [])
        .map(uuid => posts.find(post => post.uuid === uuid)).filter((post): post is Post => Boolean(post));
    const levelPosts: Post[] = postsAtLevel(posts, directoryId);
    const search: string = query.trim().toLocaleLowerCase();
    const matchingPosts: Post[] = (view === "recent" ? recent : search ? subtreePosts : levelPosts)
        .filter(post => !search || `${post.subject || ""} ${post.message || ""}`.toLocaleLowerCase().includes(search));

    useEffect(() => {
        const controller = new AbortController();
        setHomeError(false);
        void getViewerHome(controller.signal).then(setHome).catch(() => {
            if (!controller.signal.aborted) {
                setHomeError(true);
            }
        });
        return () => controller.abort();
    }, [retry]);

    useEffect(() => {
        setCategoryError(false);
        if (!categoryId || cache[categoryId]) {
            return;
        }
        const controller = new AbortController();
        void getViewerCategory(categoryId, controller.signal).then(data => {
            setCache(current => ({ ...current, [categoryId]: data }));
        }).catch(() => {
            if (!controller.signal.aborted) {
                setCategoryError(true);
            }
        });
        return () => controller.abort();
    }, [categoryId, retry]);

    useEffect(() => {
        setQuery("");
        setCheckInError("");
        window.scrollTo({ top: 0 });
    }, [location.search]);

    const navigate = (next: Record<string, string | null>): void => {
        const searchParams = new URLSearchParams();
        Object.entries(next).forEach(([key, value]) => {
            if (value) {
                searchParams.set(key, value);
            }
        });
        history.push({ pathname: "/body-and-mind", search: searchParams.toString() });
    };

    const goBack = (): void => {
        if (postId) {
            navigate({ category: categoryId, directory: directoryId, view });
        } else if (view) {
            navigate({ category: categoryId, directory: directoryId });
        } else if (directoryId) {
            navigate({ category: categoryId, directory: directory?.parentSubcategoryUuid || null });
        } else {
            navigate({});
        }
    };

    const toggleCheckIn = async (post: Post): Promise<void> => {
        if (!post.checkIn || mutation.current) {
            return;
        }
        mutation.current = true;
        setCheckInBusy(true);
        setCheckInError("");
        try {
            const checkIn: CheckIn = await updateCheckIn(post.uuid, !post.checkIn.hasUserCheckedIn);
            const updatePosts = (items: Post[]): Post[] => items.map(item => {
                if (item.checkIn?.placeId === checkIn.placeId && item.checkIn?.provider === checkIn.provider) {
                    return { ...item, checkIn: { ...item.checkIn,
                        userCheckInsCount: checkIn.userCheckInsCount, hasUserCheckedIn: checkIn.hasUserCheckedIn } };
                }
                return item;
            });
            setHome(current => current ? { ...current, uncategorisedContent: updatePosts(current.uncategorisedContent) } : null);
            setCache(current => Object.fromEntries(Object.entries(current).map(([uuid, value]) =>
                [uuid, { ...value, posts: updatePosts(value.posts) }])));
        } catch {
            setCheckInError("We couldn’t update your check in. Please try again.");
        } finally {
            mutation.current = false;
            setCheckInBusy(false);
        }
    };

    const renderPost = (post: Post, detail: boolean): React.ReactElement => <ViewerPost key={post.uuid} post={post}
        detail={detail} checkInBusy={checkInBusy}
        onOpen={() => navigate({ category: categoryId, directory: directoryId, view, post: post.uuid })}
        onBack={goBack}
        onCheckIn={() => { void toggleCheckIn(post); }} />;

    const postSection = (items: Post[], heading: string, recentSection: boolean): React.ReactElement => (
        <ViewerSection aria-label={heading}>
            <SectionHeading><div>{category && <p>{category.name}</p>}<h2>{heading}</h2></div>
                <CheckedInAction type="button" $monochrome onClick={() => navigate({ category: categoryId, directory: directoryId,
                    view: recentSection ? "recent" : "all" })}>View All</CheckedInAction>
            </SectionHeading>
            <PostCollection $horizontal>{items.map(post => renderPost(post, false))}</PostCollection>
        </ViewerSection>
    );

    const retryButton = <CheckedInAction type="button" $outline onClick={() => setRetry(value => value + 1)}>Try again</CheckedInAction>;
    if (homeError || categoryError) {
        return <ViewerMessage $error role="alert">We couldn’t load {title}. Please try again.
            <div>{retryButton}</div><div><CheckedInAction type="button" $outline onClick={() => navigate({})}>Back to {featureName}</CheckedInAction></div>
        </ViewerMessage>;
    }
    if (!home || (categoryId && !contents)) {
        return <ViewerMessage role="status">Loading {title}…</ViewerMessage>;
    }
    if ((categoryId && !category) || (directoryId && !directory) || (postId && !selectedPost)) {
        return <ViewerMessage role="status">This content is no longer available.
            <div><CheckedInAction type="button" onClick={() => navigate({})}>Back to {featureName}</CheckedInAction></div>
        </ViewerMessage>;
    }

    return <ViewerRoot>
        {postId ? <>
            {checkInError && <ViewerMessage $error role="alert">{checkInError}</ViewerMessage>}
            {renderPost(selectedPost, true)}
        </> : <>
            {!directoryId && !view ? <>
                <Hero $home={!categoryId}>
                    {categoryId ? <>
                        {category.headerVideoUrl && /\.(mp4|webm|m4v|mov)(\?|#|$)/i.test(category.headerVideoUrl)
                            ? <video src={category.headerVideoUrl} poster={category.videoThumbnailUrl || undefined}
                                autoPlay muted loop playsInline preload="metadata" aria-label={`${category.name} introduction`} />
                            : (category.headerVideoUrl || category.videoThumbnailUrl || category.iconUrl) && <img
                                src={category.headerVideoUrl || category.videoThumbnailUrl || category.iconUrl} alt="" />}
                        <CheckedInAction type="button" $round onClick={goBack} aria-label="Back">
                            <CheckedInIcon name="back" /></CheckedInAction>
                    </> : <video src="/video/bam_business.mp4" poster="/video/bam_business-poster.webp"
                        autoPlay muted loop playsInline preload="metadata"
                        aria-label={`${featureName} introduction`} />}
                    <h1>{title}</h1>
                </Hero>
            </> : <ViewerBar>
                <CheckedInAction type="button" $round onClick={goBack} aria-label="Back"><CheckedInIcon name="back" /></CheckedInAction>
                <h1>{view === "recent" ? "Recently Added" : title}</h1><span />
            </ViewerBar>}
            {categoryId && <SearchBox>
                <input aria-label={`Search ${title}`} type="search" placeholder={`Search for ${title}`}
                    value={query} onChange={event => setQuery(event.target.value)} />
                <CheckedInIcon name="search" />
            </SearchBox>}
            {!categoryId && view ? <ViewerSection>
                <PostCollection $horizontal={false}>{matchingPosts.map(post => renderPost(post, false))}</PostCollection>
            </ViewerSection> : !categoryId ? <>
                <ViewerSection><DirectoryGrid $categories>
                    {ordered(home.categories).map(item => <DirectoryTile $category key={item.uuid}
                        onClick={() => navigate({ category: item.uuid })}>
                        <span>{item.name}</span>{item.iconUrl && <img src={item.iconUrl} alt="" loading="lazy" />}
                    </DirectoryTile>)}
                </DirectoryGrid></ViewerSection>
                {home.categories.length === 0 && home.uncategorisedContent.length === 0
                    && <ViewerMessage>No places to explore yet. Please check back soon.</ViewerMessage>}
                {home.uncategorisedContent.length > 0 && postSection(home.uncategorisedContent, "Latest posts", false)}
            </> : <>
                {!view && visibleChildren.length > 0 && <ViewerSection><DirectoryGrid>
                    {visibleChildren.filter(item => !search || item.categoryName.toLocaleLowerCase().includes(search))
                        .map(item => <DirectoryTile key={item.uuid}
                            onClick={() => navigate({ category: categoryId, directory: item.uuid })}>{item.categoryName}</DirectoryTile>)}
                </DirectoryGrid></ViewerSection>}
                {view || search ? <ViewerSection>
                    {matchingPosts.length > 0 ? <PostCollection $horizontal={false}>
                        {matchingPosts.map(post => renderPost(post, false))}
                    </PostCollection> : <ViewerMessage>No posts found.</ViewerMessage>}
                </ViewerSection> : <>
                    {levelPosts.length > 0 && postSection(levelPosts, title, false)}
                    {!directoryId && recent.length > 0 && postSection(recent, "Recently Added", true)}
                    {visibleChildren.length === 0 && levelPosts.length === 0 && recent.length === 0
                        && <ViewerMessage>No posts here yet. Please check back soon.</ViewerMessage>}
                </>}
            </>}
        </>}
    </ViewerRoot>;
}
